import { isEqual, isNumber, sortBy, zip } from 'lodash';
import { SeriesOptionsType } from 'highcharts';

import { ParsedFacetExpression, PlotModel } from 'common/types';
import { Cell, Plot, SliceInfo, Value } from 'common/graphqlTypes';
import { Dashboard, GraphConfigType } from 'components/dashboards/types';
import { DashboardPlotLabelValue } from 'components/dashboards/dashboardData';
import { EventCacheData } from 'hooks/useLRQ/useEventCache';
import { ErrorResult } from 'hooks/useLRQ/useLRQ';
import { createSizedArray } from 'common/util/array';
import { escapeFacetName } from 'common/util/string';
import { getTimeDifferenceInMs } from 'common/util/time';
import { Token, TOKEN_TYPE } from 'common/util/filter/types';
import { getPQNumericValue, getPQTextValue, PQDataValue } from 'common/util/pqUtil';
import {
  findIndexOfLastToken,
  getTokenByType,
  isComplexExpression,
  isComplexMathExpression,
  isConstantExpression,
  tokenizeFilter,
} from 'common/util/filter/parse';

import * as K from 'common/constants';

// matches a custom percentile function such as p[92]
const CUSTOM_PERC_FUNC_REGEX = /^p\[(\d+)\]$/;

/**
 * This module exports Scalyr-defined pure functions for processing logic related to graphs.
 */

/**
 * @param plotData        Record of PlotModels to map color values to
 * @param colors          List of colors to map to the provided plot model
 *
 * @returns               Returns a new Record of plotModels with colors mapped to each individual plot model.
 *                        If the list of plots is longer than our list of colors, we start at the beginning
 *                        of our color list, and repeat.
 */
export const mapColorsToPlots = (
  plotData: Record<string, Array<PlotModel>> = {},
  colors: ReadonlyArray<string> = [],
): Record<string, Array<PlotModel>> => {
  if (!colors.length) {
    return { ...plotData };
  }

  const mappedPlotData: Record<string, Array<PlotModel>> = {};
  let idx = 0;
  Object.keys(plotData).forEach((section) => {
    mappedPlotData[section] = plotData[section].map((plotItem) => {
      const plotWithColor = { ...plotItem, color: colors[idx % colors.length] };
      idx += 1;
      return plotWithColor;
    });
  });

  return mappedPlotData;
};

/**
 * Utilities to help make plot query expression generation easier.
 */

/**
 * Determine if this is a facet graph or not. Returns true if so, or false if it's a rate graph.
 *
 * @param facet The plot's configured facet text (may include a function)
 */
export const isFacetGraph = (facet?: string): boolean => !!facet && facet.trim() !== K.RATE.op;

/**
 * Extract graphing function. Returns empty string if none.
 *
 * @param text The string to test (could be a filter or a facet).
 */
export const getFunction = (text = ''): string => {
  const funcToken = getTokenByType(text.trim(), TOKEN_TYPE.FUNCTION);
  return funcToken ? funcToken.text : '';
};

/**
 * Return true if the text has a graphing function.
 *
 * @param text    string to test (could be a filter or a facet).
 */
export const hasFunction = (text = ''): boolean => !!getFunction(text);

/**
 * Lycanthropy check. Returns true if the string has a 'where' token in it.
 *
 * @param text    string to test (could be a filter or a facet).
 * @returns true if the text has a 'where' token
 */
export const hasWhereClause = (test: string): boolean => !!getTokenByType(test, TOKEN_TYPE.WHERE);

/**
 * Adds a filter to a graphing function using 'where'. Best done during a full moon.
 *
 * @param into      graphing function with argument, eg 'mean(latency)'
 * @param clause    a filter, eg 'tag="cpuPing"'
 *
 * @returns graph expression, eg 'mean(latency where tag="cpuPing"')
 */
export const insertWhereClause = (into: string, clause: string): string => {
  const tokens = tokenizeFilter(into);
  const rightParenTokenIndex = findIndexOfLastToken(tokens, (token: Token) => token.type === TOKEN_TYPE.RIGHT_PAREN);
  const rightParenPos = tokens[rightParenTokenIndex]?.start || into.length - 1;

  return into.substring(0, rightParenPos) + ' where ' + clause + ')';
};

/*
 * Extract simple facet expression into a facet function and a facet variable.
 *
 *   mean(latency) => { facetFunction: 'mean', facetVariable: 'latency' }
 *   p[73](latency) => { facetFunction: 'p[73]', facetVariable: 'latency' }
 *   p(latency, 73) => { facetFunction: 'p[73]', facetVariable: 'latency' }
 *   latency => { facetVariable: 'latency' }
 *   mean => { facetFunction: 'mean' }
 *
 * @param facetExpression   word or function with argument
 * @return object with 'facetFunction' and 'facetVariable' properties
 */
export const extractFacetExpression = (facetExpression = ''): ParsedFacetExpression => {
  const tokens = tokenizeFilter(facetExpression);
  let facetFunction = '';
  let facetVariable = '';
  if (tokens.length === 1) {
    // just one token - assume it's a facet
    facetVariable = tokens[0].text;
  } else if (tokens.length >= 3) {
    // handle alternate form of custom percentile function, eg 'p(value, 87)' by capturing function as p[]
    facetFunction = tokens[0].text === 'p' ? 'p[' + tokens[4].text + ']' : tokens[0].text;
    facetVariable = tokens[2].text;
  }

  return { facetFunction, facetVariable };
};

/**
 * Create a GraphQL plot query expression for custom percentiles.
 *
 * @param filter  The plot's configured filter text
 * @param facet   The plot's configured facet text -- either p[n](facet) or p(facet, n)
 */
export const createCustomPercentileExpression = (facet: string, filter: string): string => {
  const func = getFunction(facet);
  const facetTokens = tokenizeFilter(facet);
  const fieldToken = getTokenByType(facet, TOKEN_TYPE.WORD);
  const field = fieldToken ? fieldToken.text : '';
  let percentile = '';

  // Special case for p[n](f)
  const parts = func.match(CUSTOM_PERC_FUNC_REGEX);
  if (parts) {
    // If we have a 'where' clause, we're done.
    // If we don't have exactly four tokens (function, paren, facet, paren), return as is and let the user get a parsing error.
    if (hasWhereClause(facet) || facetTokens.length !== 4) {
      return facet;
    }
    percentile = parts[1];
  } else {
    // If we don't have exactly four tokens (function, paren, facet, comma, number, paren), return as is and let the user get a parsing error.
    if (facetTokens.length !== 6) {
      return facet;
    }
    const percentileToken = getTokenByType(facet, TOKEN_TYPE.NUMBER);
    percentile = percentileToken ? percentileToken.text : '';
  }

  return insertWhereClause(`p[${percentile}](${field})`, filter || `${field}=*`);
};

/**
 *  Used to determine if the user entered filter is already formatted into a complex expression
 *
 * @param filter  The filter as the user has entered it
 * @return true if the filter is a complex graphing expression
 */
export const isComplexGraphingExpression = (filter: string): boolean =>
  (isComplexExpression(filter) && hasWhereClause(filter)) ||
  isConstantExpression(filter) ||
  isComplexMathExpression(filter);

/**
 * Create a GraphQL plot query expression.
 *
 * todo: add unit tests
 *
 * @param filter         The plot's configured filter text (may be complex)
 * @param facet          The plot's configured facet (either "rate" or a function with a facet argument)
 * @param breakdownFacet The plot's configured breakdown facet
 * @param countFunction  For rate charts -- usually "count" for bar charts and "countPerSec" for line charts
 */
export const createSearchGraphExpression = ({
  filter = '',
  facet = '',
  breakdownFacet = '',
  countFunction = 'countPerSec',
}): string => {
  let trimmedFilter = filter.trim();

  // if filter already looks like a graph expression, just return it
  if (isComplexGraphingExpression(trimmedFilter)) {
    return trimmedFilter;
  }

  let escapedFacet = facet.trim();

  // If facet is a function call, escape only the facet name.
  // Note: now that we officially allow dots in facet names, there may be no need to escape them
  if (isComplexExpression(facet)) {
    const facetTokens = tokenizeFilter(escapedFacet);
    // tokenizer will break apart a facet with special characters, so grab the substring
    // handle various forms: p90(value) p90(value.max) p(value, 95) p(value.max, 95)
    const endToken = facetTokens.find(
      (token, index) => index > 2 && [TOKEN_TYPE.COMMA, TOKEN_TYPE.RIGHT_PAREN].includes(token.type as TOKEN_TYPE),
    );
    const endPos = endToken ? endToken.start : facetTokens[facetTokens.length - 1].start;
    escapedFacet =
      facetTokens[0].text +
      '(' +
      escapeFacetName(facet.substring(facetTokens[1].end, endPos)) +
      facet.substring(endPos);
  } else {
    escapedFacet = escapeFacetName(escapedFacet);
  }

  const escapedBreakdownFacet = escapeFacetName(breakdownFacet.trim());

  // Rate graph, possibly with a breakdown facet
  if (!escapedFacet || escapedFacet === K.RATE.op) {
    if (hasFunction(trimmedFilter)) {
      return trimmedFilter;
    }
    if (!trimmedFilter && escapedBreakdownFacet) {
      trimmedFilter = 'true';
    }
    return insertWhereClause(`${countFunction}(${escapedBreakdownFacet || 'value'})`, trimmedFilter || '""');
  }

  // Otherwise, facet graph

  // Special case for custom percentiles, which take one of two forms: p[n](f) or p(f, n)
  // Graph expressions use the first form.
  const func = getFunction(escapedFacet);
  if (func === 'p' || /^p\[\d+\]$/.test(func)) {
    return createCustomPercentileExpression(escapedFacet, trimmedFilter);
  }

  if (hasFunction(escapedFacet)) {
    if (hasWhereClause(escapedFacet)) {
      return escapedFacet;
    }
    if (trimmedFilter) {
      return insertWhereClause(escapedFacet, trimmedFilter);
    }
    // Facet function is present, but no filter is, modify for breakdownFacet if needed
    return insertWhereClause(escapedFacet, escapedBreakdownFacet ? 'true' : '""');
  }

  // Default to mean(). If filter is present, add it. Otherwise modify with breakdown facet if needed.
  if (trimmedFilter) {
    return insertWhereClause(`mean(${escapedFacet})`, trimmedFilter);
  }
  return insertWhereClause(`mean(${escapedFacet})`, escapedBreakdownFacet ? 'true' : '""');
};

export const isFacetPlotVisible = (
  plotName: string,
  showGraphs: string,
  hideGraphs: string,
  facet: string,
): boolean => {
  const parsedFacet = extractFacetExpression(facet);
  let defaultFacetFn = parsedFacet?.facetFunction || K.MEAN.op;
  defaultFacetFn = defaultFacetFn === K.SUM_PER_SEC.name ? K.SUM_PER_SEC.op : defaultFacetFn;
  const showGraphsList = showGraphs ? JSON.parse(showGraphs) : [];
  const hideGraphsList = hideGraphs ? JSON.parse(hideGraphs) : [];
  const isDefaultPlot = plotName === defaultFacetFn;

  if (!hideGraphsList.includes(plotName)) {
    return isDefaultPlot || showGraphsList.includes(plotName);
  }

  return false;
};

export const isComparePlotVisible = (plotName: string, showGraphs: string, hideGraphs: string): boolean => {
  const showGraphsList = showGraphs ? JSON.parse(showGraphs) : [];
  const hideGraphsList = hideGraphs ? JSON.parse(hideGraphs) : [];
  const isDeltaPlot = plotName === 'delta';

  if (!hideGraphsList.includes(plotName)) {
    return !isDeltaPlot || showGraphsList.includes(plotName);
  }

  return false;
};

export const isPlotVisible = (plotName: string, hideGraphs: string): boolean => {
  const hideGraphsList = hideGraphs ? JSON.parse(hideGraphs) : [];
  return !hideGraphsList.includes(plotName);
};

export const getFacetValuePlotGroups = (
  xValues: Array<number>,
  plots: Array<Plot>,
  showGraphs: string,
  hideGraphs: string,
  facet: string,
  graphStyle: string,
): Record<string, PlotModel[]> => {
  const plotGroups: Record<string, PlotModel[]> = {};

  Object.entries(K.GROUPED_FACET_FUNCTIONS).forEach(([group, plotList]) => {
    const plotGroup: PlotModel[] = [];

    const counts =
      facet && facet !== K.RATE.op ? plots.find((plot) => plot?.fn?.operator === K.COUNT)?.samples : undefined;

    plotList.forEach((plot) => {
      const plotEntry = plots.find((p) => p?.fn?.operator === plot.op);

      const isSumFacetFunction = plotEntry?.fn.operator === K.SUM.op;
      const shouldAppendSumFacetFunction = isSumFacetFunction && graphStyle === K.CHART_TYPES.STACKED_BAR;
      const isValidPlotEntry = !isSumFacetFunction || shouldAppendSumFacetFunction;

      if (plotEntry && isValidPlotEntry) {
        plotGroup.push({
          name: plot.name,
          visible: isFacetPlotVisible(plot.op, showGraphs, hideGraphs, facet),
          color: '',
          data: zip(
            xValues,
            createSizedArray({
              filler: 0,
              size: xValues.length,
              sourceArray: plotEntry?.samples,
            }),
          ),
          op: plot.op,
          counts,
        });
      }
    });
    if (plotGroup.length > 0) {
      plotGroups[group] = plotGroup;
    }
  });

  return plotGroups;
};

export const getBreakdownPlotGroups = (
  xValues: Array<number>,
  plots: Array<Plot>,
  breakdownFacet: string,
  facet: string,
  graphStyle: string,
  hideGraphs: string,
): Record<string, PlotModel[]> => {
  const filteredCounts = plots.filter((plot) => plot?.fn?.operator === K.COUNT);

  let breakdownPlots;
  if (!facet && graphStyle === K.CHART_TYPES.STACKED_BAR) {
    breakdownPlots = plots;
  } else if (facet && facet === K.RATE.op && graphStyle === K.CHART_TYPES.STACKED_BAR) {
    breakdownPlots = plots;
  } else {
    breakdownPlots = plots.filter((plot) => plot?.fn?.operator !== K.COUNT);
  }

  return {
    [breakdownFacet]: breakdownPlots
      .map((plot) => {
        const counts =
          facet && facet !== K.RATE.op
            ? filteredCounts.find((count) => count.label === plot.label)?.samples
            : undefined;

        return {
          name: plot?.label,
          op: plot?.label,
          visible: isPlotVisible(plot?.label, hideGraphs),
          color: '',
          counts,
          data: zip(
            xValues,
            createSizedArray({
              filler: 0,
              size: xValues.length,
              sourceArray: plot?.samples,
            }),
          ),
        };
      })
      .slice(0, 19),
  };
};

export const getComparePlotGroups = (
  xValues: Array<number>,
  plots: Array<Plot>,
  compareTimeOffset: string,
  plotColors: Array<string>,
  hideGraphs: string,
  showGraphs: string,
  facet: string,
): Record<string, PlotModel[]> => {
  let facetFunction = K.MEAN.op;
  if (facet && facet !== K.RATE.op) {
    const parsedFacet = extractFacetExpression(facet);
    facetFunction = parsedFacet?.facetFunction || K.MEAN.op;
  }

  const compareName = (): string => {
    switch (compareTimeOffset) {
      case '1h':
        return `${facetFunction} prev hour`;
      case '4h':
        return `${facetFunction} prev 4 hour`;
      case '1d':
        return `${facetFunction} prev day`;
      case '1w':
        return `${facetFunction} prev week`;
      case '2w':
        return `${facetFunction} prev 2 weeks`;
      default:
        return 'Previous';
    }
  };
  // insure yAxis values are in sync with our xAxis length
  const firstPlotValues = createSizedArray({ filler: 0, size: xValues.length, sourceArray: plots[0]?.samples });
  const comparePlotValues = createSizedArray({ filler: 0, size: xValues.length, sourceArray: plots[1]?.samples });
  const deltaPlotValues = plots[0]?.samples.map((yValue, index) => Math.abs(yValue - plots[1]?.samples[index]));

  const COMPARISON_LABEL = 'compare to';
  const COMPARISON_PLOT_COLOR = plotColors[3];

  const DELTA_LABEL = 'delta';
  const COMPARISON_DELTA_PLOT_COLOR = plotColors[2];

  return {
    functions: [
      {
        name: facetFunction,
        op: 'main', // legacy op so angular/ new ui switch works when plots toggled on or off
        visible: isPlotVisible('main', hideGraphs),
        color: plotColors[0],
        data: zip(xValues, firstPlotValues),
      },
    ],
    [COMPARISON_LABEL]: [
      {
        name: compareName(),
        op: 'shifted', // legacy op so angular/ new ui switch works when plots toggled on or off
        visible: isPlotVisible('shifted', hideGraphs),
        color: COMPARISON_PLOT_COLOR,
        data: zip(xValues, comparePlotValues),
      },
    ],
    [DELTA_LABEL]: [
      {
        name: 'delta',
        op: 'delta', // legacy op so angular/ new ui switch works when plots toggled on or off
        visible: isComparePlotVisible('delta', showGraphs, hideGraphs),
        color: COMPARISON_DELTA_PLOT_COLOR,
        data: zip(xValues, deltaPlotValues),
      },
    ],
  };
};

export const getStandardGraphPlot = (
  xValues: Array<number>,
  plots: Array<Plot>,
  filter: string,
  hideGraphs: string,
  plotColors: Array<string>,
): Record<string, PlotModel[]> => {
  let name = K.MEAN.name;
  let op = K.MEAN.op;

  // If the filter contains a complex expression with 1 function, we want the label to show that function
  if (filter && isComplexExpression(filter)) {
    const tokens = tokenizeFilter(filter);
    const functionTokens = tokens.filter((token) => token.type === TOKEN_TYPE.FUNCTION);

    if (functionTokens.length === 1) {
      const functionToken = functionTokens[0];
      const facetFunction = K.FACET_FUNCTIONS.find(
        (facetFn) => functionToken.text === facetFn.name || functionToken.text === facetFn.op,
      );
      name = facetFunction?.name || K.MEAN.name;
      op = facetFunction?.op || K.MEAN.op;
    }
  }

  return {
    functions: [
      {
        name,
        op,
        visible: isPlotVisible(op, hideGraphs),
        color: plotColors[0],
        data: zip(xValues, createSizedArray({ filler: 0, size: xValues.length, sourceArray: plots[0]?.samples })),
      },
    ],
  };
};

/**
 * Takes the list of plots and the xAxis values and generates the groups of plots for display in the legend.
 * //TODO: initial visibility of each plot needs to honor show/hide plot url params with sensible defaults
 *
 * @param xValues             List of xAxis values
 * @param plots               List of plot results to map to plot groups
 * @param facet               facet parameter value
 * @param breakdownFacet      breakdownFacet parameter value
 * @param compareTimeOffset   the time value that offsets the current start-time range
 * @param graphStyle          The style of graph
 * @param showGraphs          a string array of params for which plots to show
 * @param hideGraphs          a string array of params for which plots to hide
 * @param plotColors          an array color strings to use for the plots
 */
export const getPlotGroups = (
  xValues: Array<number>,
  plots: Array<Plot>,
  filter: string,
  facet: string,
  breakdownFacet: string,
  compareTimeOffset: string,
  graphStyle: string,
  showGraphs: string,
  hideGraphs: string,
  plotColors: Array<string>,
): Record<string, PlotModel[]> => {
  // Facet value graph
  if (isFacetGraph(facet) && !breakdownFacet && !compareTimeOffset && !isComplexGraphingExpression(filter)) {
    return mapColorsToPlots(
      getFacetValuePlotGroups(xValues, plots, showGraphs, hideGraphs, facet, graphStyle),
      plotColors,
    );
  }

  // Breakdown graph
  if (breakdownFacet) {
    return mapColorsToPlots(
      getBreakdownPlotGroups(xValues, plots, breakdownFacet, facet, graphStyle, hideGraphs),
      plotColors,
    );
  }

  // Comparison graph
  if (compareTimeOffset) {
    return getComparePlotGroups(xValues, plots, compareTimeOffset, plotColors, hideGraphs, showGraphs, facet);
    // Standard rate graph
  }

  return getStandardGraphPlot(xValues, plots, filter, hideGraphs, plotColors);
};

/**
 * Calculate the display values for a plot. Calculates the average of the data points based on
 * a given range, unless the plot is min/max, then we return the min and max value respectively.
 *
 * @param plot                  PlotModel object with series data and counts/weight
 * @param appliedAggregateFunc  name of aggregate function, if an aggregate function was applied to the facet (breakdown facet value graphs)
 * @param start                 xAvis start value of selected range
 * @param end                   xAxis end value of selected range
 * @param timeStamps            list of ms timestamps for the current chart
 */
export const getDisplayValue = (
  plot: PlotModel,
  appliedAggregateFunc: string | null,
  start?: number,
  end?: number,
  timeStamps: number[] = [],
): number | null => {
  if (!start || !end || !timeStamps.length) {
    return null;
  }

  const nonWeightedAverageSeries = [K.MIN.op, K.MAX.op, K.SUM_PER_SEC.op];
  let isWeightedAverage = plot.op && !nonWeightedAverageSeries.includes(plot.op);
  if (appliedAggregateFunc) {
    isWeightedAverage = !nonWeightedAverageSeries.includes(appliedAggregateFunc);
  }

  const samples = plot?.data || [];
  let counts: number[] = plot?.counts || [];
  const useWeightedAverage = isWeightedAverage && !!counts;
  counts = counts.length && useWeightedAverage ? counts : new Array(samples.length).fill(1);

  let sum = 0;
  let min = null;
  let max = null;
  let totalCount = 0;
  for (let i = 0; i < timeStamps.length; i += 1) {
    const dataPoint = samples?.[i]?.[1];
    if (timeStamps[i] > end) break;
    if (timeStamps[i] >= start && isNumber(dataPoint)) {
      totalCount += counts[i];
      sum += counts[i] * dataPoint;

      if (!min || dataPoint < min) min = dataPoint;
      if (!max || dataPoint > max) max = dataPoint;
    }
  }

  if (plot.op === K.MIN.op || appliedAggregateFunc === K.MIN.op) return min;
  if (plot.op === K.MAX.op || appliedAggregateFunc === K.MAX.op) return max;
  return totalCount ? sum / totalCount : null;
};

/**
 * Get delta of plot values between the specified start and end time
 *
 * @param plot        PlotModel object with series data
 * @param start       xAvis start value of selected range
 * @param end         xAxis end value of selected range
 * @param timeStamps  list of ms timestamps for the current chart
 */
export const getDelta = (plot: PlotModel, start?: number, end?: number, timeStamps: number[] = []): number | null => {
  if (!start || !end || !timeStamps.length) return 0;

  const samples = plot.data;
  let first = null;
  let last = null;
  for (let i = 0; i < timeStamps.length; i += 1) {
    const dataPoint = samples[i][1]; // grab yAxis value from coordinate tuple
    if (timeStamps[i] > end) break;
    if (timeStamps[i] >= start && isNumber(dataPoint)) {
      if (!isNumber(first)) first = dataPoint; // First numeric value
      last = dataPoint;
    }
  }

  return isNumber(last) && isNumber(first) ? last - first : null;
};

/**
 * Truncate text to a shorter length using an ellipsis.
 *
 * @param maxValue A number to use as the upper range of values
 */
export const elliptifyLabelText = (text: string, maxLength: number, midEllipsis?: boolean): string => {
  if (text.length > maxLength) {
    if (midEllipsis) {
      // Include the last 4 characters to help differentiate blahblahblah1000, blahblahblah1001, etc.
      return text.substr(0, maxLength - 5) + ' … ' + text.substr(text.length - 4);
    }
    return text.substr(0, maxLength - 1) + '…';
  }

  return text;
};

/**
 * Decide how many decimal places to show based on the maximum value.
 *
 * @param maxValue A number to use as the upper range of values
 */
export const calcTootipDecimals = (maxValue: number): number => {
  if (maxValue === 0 || maxValue > 1000) {
    return 0;
  }
  if (maxValue < 1) {
    return 5;
  }
  if (maxValue < 5) {
    return 3;
  }
  return 2;
};

/**
 * Figures out where the first bar should be placed on the graph.  The backend gives us the
 * center point that should be SHOWN on the graph, but we actually have to place the bar in
 * the correct place for where the entire bucket should be placed in order to get highcharts
 * to render everything correctly. For instance we might have a default bucket size of 5
 * minutes, and a time range of 10:18 - 10:30, your first bucket is going to be from
 * 10:18 - 10:20, and the center would be at 10:19 for that bucket.  The next bucket would be
 * from 10:20 - 10:25 (center at 10:22:30), and then 10:25 - 10:30 (center at 10:27:30).  So
 * the xAxis points we get would be 10:19, 10:22:30, and 10:27:30.  We actually need the first
 * bar to be placed at 10:17:30 (half way between 10:15 and 10:20. This produces the correct
 * xAxis point for the first bar based on the default and first bucket widths.
 *
 * @param minAxis   min X axis value
 * @param maxAxis   max X axis value
 * @param sliceInfo SliceInfo contains defaultSize, firstSize, and lastSize
 */
export const adjustBucketAtMinMax = (minAxis: number, maxAxis: number, sliceInfo: SliceInfo): Array<number> => {
  const { defaultSize, firstSize, lastSize } = sliceInfo;
  const bucketWidthDiffFirst = defaultSize - firstSize;
  const bucketWidthDiffLast = defaultSize - lastSize;

  const adjustedMix = minAxis - bucketWidthDiffFirst / 2;
  const adjustedMax = maxAxis + bucketWidthDiffLast / 2;

  return [adjustedMix, adjustedMax];
};

export enum LRQ_STATUS {
  IN_PROGRESS = 'inProgress', // the LRQ is still in flight
  NO_RESULTS = 'noResults', // no results were returned (not necessarily an error)
  NO_MATCHES = 'noMatches', // no matching event data (not necessarily an error)
  HAS_ERRORS = 'hasErrors', // has errors to display
  DATA_READY = 'dataReady', // is ready to go
}

/**
 * Examine the LRQ PlotData or PqResultData cache and return the state of the request
 *
 * @param results The data return value from useEventCache
 * @param errors  Any potential error return value from useEventCache
 */
export const getLrqStatus = (results: EventCacheData[], errors: ErrorResult[], isPlotData: boolean): LRQ_STATUS => {
  if (errors?.some((error) => error)) {
    // always check for errors first
    return LRQ_STATUS.HAS_ERRORS;
  }
  if (results === undefined) {
    return LRQ_STATUS.IN_PROGRESS;
  }
  const hasAllResults = results.every((result) => result.isDone);
  if (!hasAllResults) {
    return LRQ_STATUS.IN_PROGRESS;
  }

  if (isPlotData) {
    // Not sure what would cause this case, where at least one result says it is done but has no data
    const hasMissingPlotData = results.some((result) => !result.plotData || !result.plotData.plots);
    if (hasMissingPlotData) {
      return LRQ_STATUS.IN_PROGRESS;
    }
    // Would be much more efficient to just check matchCount for this, but the API support for it is inconsistent
    const hasNoPlotMatches = results.every((result) =>
      result.plotData.plots.every((plot) => plot.samples.every((sample) => sample === null)),
    );
    if (hasNoPlotMatches) {
      return LRQ_STATUS.NO_MATCHES;
    }
  } else if (!results.length) {
    // is a power query returning an empty results array
    return LRQ_STATUS.NO_RESULTS;
  }

  return LRQ_STATUS.DATA_READY;
};

/**
 * Based on the graphStyle and lineSmoothing URL params, determine the type of chart to render.
 *
 * @param graphStyle     the style of graph: stacked, line etc.
 * @param lineSmoothing  type of line smoothing, if any.
 */
export const getChartType = (
  graphStyle?: string,
  lineSmoothing?: string,
): SeriesOptionsType[keyof SeriesOptionsType] => {
  if (graphStyle === K.CHART_TYPES.STACKED_BAR) {
    return K.SERIES_TYPES.COLUMN;
  }

  if (lineSmoothing === K.LINE_OPTIONS.SMOOTH_CURVES && graphStyle === K.CHART_TYPES.LINE) {
    return K.CHART_TYPES.SPLINE;
  }

  if (lineSmoothing === K.LINE_OPTIONS.SMOOTH_CURVES && graphStyle === K.CHART_TYPES.STACKED) {
    return K.CHART_TYPES.AREA_SPLINE;
  }

  if (graphStyle === K.CHART_TYPES.STACKED) {
    return K.CHART_TYPES.AREA;
  }

  return graphStyle || K.CHART_TYPES.LINE;
};

/**
 * Examines two dashboards and return true if any non-layout changes ocurred
 *
 * @param dashboard     A dashboard to compare
 * @param prevDashboard The dashboard to compare it with
 */
export const isConfigChanged = (dashboard: Dashboard | null, prevDashboard: Dashboard | undefined | null): boolean => {
  if (
    !dashboard ||
    !prevDashboard ||
    !isEqual(sortBy(dashboard.params), sortBy(prevDashboard.params)) ||
    !isEqual(sortBy(dashboard.filters), sortBy(prevDashboard.filters))
  ) {
    return true;
  }
  if (!isEqual(dashboard.config.options, prevDashboard.config.options)) {
    return true;
  }
  const stripLayouts = (graphs: string | GraphConfigType[]): GraphConfigType[] =>
    (graphs as GraphConfigType[]).map((graph) => ({ ...graph, layout: undefined }));
  const graphs = stripLayouts(dashboard.config.graphs);
  const prevGraphs = stripLayouts(prevDashboard.config.graphs);
  if (!isEqual(graphs, prevGraphs)) {
    return true;
  }
  if (!isEqual(dashboard.config.tabName, prevDashboard.config.tabName)) {
    return true;
  }
  return false;
};

/**
 * Adjust slices for line graphs such that the time interval for each slice is at least 1 second
 *
 * Legacy charts only respected the barWidth value for bar charts, despite the support on the graph view page.
 *
 * @param startTime  The start time as an abbreviation or absolute time
 * @param endTime    The end time as an abbreviation or absolute time (may be an empty string for "now")
 * @param lineSlices Integer value for the number of points in a line chart (already scaled by chart layout width)
 * @param graphStyle String value for the chart type of the graph
 */
export const limitSliceGranularity = (
  startTime: string,
  endTime: string,
  lineSlices: number,
  graphStyle: string,
): number => {
  const timeSpanMs = getTimeDifferenceInMs({ startTime, endTime }, null);
  const oneSecondMs = 1000;
  let slices = lineSlices;

  if (timeSpanMs / slices < oneSecondMs && graphStyle === K.CHART_TYPES.LINE) {
    const granularSecondSlices = Math.floor(timeSpanMs / oneSecondMs);
    slices = granularSecondSlices >= 1 ? granularSecondSlices : 1;
  }
  return slices;
};

/**
 *
 * Gets the series data for both pie/donut graphs. When the number of slices is greater than
 * the MAX_PIE_SLICES, then it allocates the rest into and "Other" slice
 *
 * @param data              The power query result data cells/values
 * @param valueCol          The index of the value column
 * @param labelCol          The index of the label column
 * @param maxPieSlices      The maximum number of slices for a pie/donut
 */

export const getPieSeriesData = (
  data: Array<Cell[] | Value[]>,
  valueCol: number,
  labelCol: number,
  maxPieSlices = K.DEFAULT_MAX_PIE_SLICES,
): Array<DashboardPlotLabelValue> => {
  const seriesData: Array<DashboardPlotLabelValue> = [];

  const valuesColumn = data.map((rowValue: Cell[] | Value[]) => getPQNumericValue(rowValue[valueCol] as PQDataValue));
  const totalValue = valuesColumn.reduce((total, value) => total + getPQNumericValue(value));
  const calcPercent = (value: number, total: number): string => Number((value / total) * 100.0).toFixed(1) + '%';

  // sort row values in descending order
  data.sort((rowA: Cell[] | Value[], rowB: Cell[] | Value[]) => {
    const yA = getPQNumericValue(rowA[valueCol] as PQDataValue);
    const yB = getPQNumericValue(rowB[valueCol] as PQDataValue);
    return yB - yA;
  });

  // append row values to seriesData
  for (let i = 0; i < data.length && i < maxPieSlices; i++) {
    const y = getPQNumericValue(data[i][valueCol] as PQDataValue);
    const name = getPQTextValue(data[i][labelCol] as PQDataValue);
    const percent = calcPercent(y, totalValue);
    seriesData.push({ name, y, percent });
  }

  // add an "Other" slice if the amount of slices exceeds a maximum X amount of slices
  if (data.length > maxPieSlices) {
    let totalY = 0;
    for (let i = maxPieSlices; i < data.length; i++) {
      const y = getPQNumericValue(data[i][valueCol] as PQDataValue);
      totalY += y;
    }
    const otherValue = {
      name: 'Other',
      y: totalY,
      percent: calcPercent(totalY, totalValue),
    };

    seriesData.push(otherValue);
  }

  return seriesData;
};
